import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import "@css/temp_wanted.css";
import { images } from "@img/imagesMap";
import type * as type from "@vTypes";
import * as func from "@components/feature/roster_wanted/script";
import LoadingDots from "@components/LoadingDots";
import ModalSubmittedWantedlist from "@components/ModalSubmittedWantedlist";
import ErrorComponent from "@components/ui/error";
import SelectShiftTypeButton from "@components/feature/roster_wanted/SelectShiftTypeButton";
import TableSelectDate from "@components/feature/roster_wanted/TableSelectDate";
import PreferenceList from "@components/feature/roster_wanted/PreferenceList";
import { useQueryClient } from "@tanstack/react-query";
import type { NurseResponse } from "@vTypes/nurse-type";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { cn } from "@utils/cn";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth";
import { ArrowRight, Info } from "lucide-react";
import CustomModal from "@/components/modal/CustomModal";
import { useUnsavedChanges } from "@hooks/weekly-off/useUnsavedChanges";
import SetDeadLine from "@/components/feature/roster_create/SetDeadLine";
import { useGroupStore } from "@/store/groupStore";

import HeadNurseHeader from "@/components/feature/roster_wanted/HeadNurseHeader";
import { toast } from "@/components/toast/CustomToast";
import { createPortal } from "react-dom";
import { useRosterWantedResources } from "./hooks/useRosterWantedQueries";
import {
  useInvokeAndSavePreference,
  useSaveCalendarData,
  useFinalSubmit,
  useRetractSubmission,
} from "./hooks/useRosterWantedMutations";
import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";
import { resolveShiftFromWantedEntry } from "@/utils/wanted-shift-util";
import { sortNurseListByRoleThenSequence } from "@utils/common-util.ts";
import {
  SoftPillButton,
  softPillButtonLabelClassName,
} from "@/components/ui/button/soft-pill-button";

interface Type_ShiftEntry {
  shift: string;
  comment: string | null;
}
type CalendarData = Record<string, Type_ShiftEntry>;

interface SelectCalendarEntry {
  type: string;
  comment: string | null;
}

interface Type_SelectCalendar {
  [date: string]: SelectCalendarEntry;
}

// Warning 타입 (백엔드 응답과 일치)
interface Warning {
  message: string;
  excluded_items: {
    day: number;
    shift: string;
    date: string;
  }[];
  existing_off_count: number;
  excluded_count: number;
  limit: number;
}

// 모달 타입
type ModalType =
  | { type: "submittedList"; isViewOnlyOff: boolean; showWeeklyOff: boolean }
  | { type: "deadline" }
  | { type: "deleteShift"; date: string; shiftType: string }
  | { type: "totalExceeded" }
  | { type: "dateExceeded"; date: string }
  | { type: "warning"; warning: Warning }
  | null;

export default function Roster_wanted() {
  // 스크롤 위치 state 추가
  const [modalScrollTop, setModalScrollTop] = useState(0);

  const queryClient = useQueryClient();
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();
  /** 전체 직원 원티드/신청 현황 — 수간호사·시스템 관리자만 */
  const canViewAllStaffWantedOverview = isAdmin || !!loginUser?.is_head_nurse;

  const [activeDate, setActiveDate] = useState<{
    year: number;
    month: number;
  } | null>(null);
  const [fullNurseSchema, setFullNurseSchema] = useState<NurseResponse[]>([]);
  const [nurseSchema_IdName, setNurseSchema_IdName] = useState<
    { nurse_id: string; name: string }[]
  >([]);

  const [fullWanted, setFullWanted] = useState<type.WantedAll[]>([]);

  const [preferenceSchedule, setPreferenceSchedule] =
    useState<type.PreferenceSchedule[]>();

  /* 개별 데이터 */

  // textarea에 포커스 여부
  const [isFocusedInTextarea, setIsFocusedInTextarea] =
    useState<boolean>(false);

  // LLM 입력내용
  const [wantedInput, setWantedInput] = useState<string | null>("");
  const [hideExampleList, setHideExampleList] = useState<boolean>(false);

  // 제출 여부
  const [submittedStatus, setSubmittedStatus] = useState<
    "empty" | "requested" | "submitted" | "closed"
  >("empty");

  // 테이블 관련
  const [formattedCalendarData, setFormattedCalendarData] =
    useState<CalendarData>({});
  const [selectedCalendarData, setSelectedCalendarData] =
    useState<Type_SelectCalendar>({});
  const [availableShifts, setAvailableShifts] = useState<ShiftReturnType>([]);
  const [defaultShiftCode, setDefaultShiftCode] = useState<ShiftReturnType>([]);

  const [preferenceData, setPreferenceData] =
    useState<type.PreferenceData | null>(null);

  // 캘린더 내 주휴 관련
  const [myWeeklyOffIndex, setMyWeeklyOffIndex] = useState<number | null>(null);

  // 달력 관련
  const [holidayDates, setHolidayDates] = useState<string[]>([]);

  const [isSubmitRequest, setIsSubmitRequest] = useState<boolean>(false);

  // 2번 탭
  // 체크박스 값 저장
  /** 미선택 — 하드코딩 D 제거, 캘린더 클릭 전 근무 유형 선택 필요 */
  const [selectedShiftType, setSelectedShiftType] = useState<string>("");

  // const isExistWanted = (bookmarkData && bookmarkData.length > 0);
  const isExistWanted = submittedStatus !== "empty";

  // 페이지 이탈 방지
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialSelectedCalendarData, setInitialSelectedCalendarData] =
    useState<Type_SelectCalendar>({});

  const [initialWantedInput, setInitialWantedInput] = useState<string | null>(
    ""
  );

  const { selectedGroupId } = useGroupStore();

  // 모달관련 state
  const [confirmModal, setConfirmModal] = useState<ModalType>(null);
  const wantedButtonRef = useRef<HTMLButtonElement>(null);

  const isMyGroupId =
    loginUser?.hn_auth === "HN" &&
    loginUser?.original_group_id === selectedGroupId;

  useEffect(() => {
    const mainContainer = document.querySelector(
      ".c_main__container"
    ) as HTMLElement;

    if (!mainContainer) return;

    if (confirmModal !== null) {
      mainContainer.style.overflow = "hidden";
    } else {
      mainContainer.style.overflow = "";
    }

    return () => {
      mainContainer.style.overflow = "";
    };
  }, [confirmModal]);

  // 저장 제출 모달 스크롤 락 useEffect
  useEffect(() => {
    if (isSubmitRequest) {
      const mainContainer = document.querySelector(
        ".c_main__container"
      ) as HTMLElement;

      if (mainContainer) {
        // 현재 스크롤 위치 저장
        setModalScrollTop(mainContainer.scrollTop);
        mainContainer.style.overflow = "hidden";
        mainContainer.style.position = "relative";
      }

      return () => {
        if (mainContainer) {
          mainContainer.style.overflow = "";
          mainContainer.style.position = "";
        }
      };
    }
  }, [isSubmitRequest]);

  // 일반 간호사는 토큰으로 조회하므로 group_id 불필요, 관리자는 선택된 그룹 ID 사용
  const gid = isAdmin ? (selectedGroupId ?? "") : "";

  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const appliedWantedUrlKey = useRef<string | null>(null);

  const setActiveDateSynced = useCallback(
    (date: { year: number; month: number } | null) => {
      setActiveDate(date);
      if (date) {
        const sp = new URLSearchParams(location.search);
        sp.set("year", String(date.year));
        sp.set("month", String(date.month));
        appliedWantedUrlKey.current = `${gid}|?${sp.toString()}`;
        setSearchParams(sp, { replace: true });
      }
    },
    [gid, location.search, setSearchParams]
  );

  // 페이지 진입 시 body에 클래스 추가 (배경색 적용을 위함.)
  useEffect(() => {
    document.body.classList.add("page-roster-wanted");

    return () => {
      document.body.classList.remove("page-roster-wanted");
    };
  }, []);

  // textarea 높이 자동 조정 함수
  const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    element.style.height = element.scrollHeight + "px";
    setHideExampleList(element.scrollHeight > 40);
  };

  // textarea 내용 변경 시 높이 자동 조정
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWantedInput(e.target.value);
    adjustTextareaHeight(e.target);
  };

  // 통합 리소스 훅 사용
  const {
    data: resources,
    isLoading,
    error: pageError,
  } = useRosterWantedResources({
    year: activeDate?.year,
    month: activeDate?.month,
    groupId: gid,
    nurseId: loginUser?.nurse_id,
    isHeadNurse: loginUser?.is_head_nurse,
  });

  const {
    nurseList,
    wantedList,
    shiftsData,
    shiftLookupMaps,
    holidayDatesData,
    myWeeklyOffData,
    wantedConfig,
    wantedStatus,
    preferencesLatestData,
    preferencesAllData,
    currentWanted,
    isCurrentWantedClosed,
    serverConfig,
  } = resources || {};
  console.log("shiftsData:", shiftsData);

  // Mid 사용 여부
  const isUseMid = serverConfig?.use_mid ?? false;

  // 원티드 희망사항 제출 Mutation 훅
  const {
    mutate: submitWantedRequest,
    isPending: isSubmitWantedPending,
    isSuccess: isSubmitWantedSuccess,
    isError: isSubmitWantedError,
    reset: resetSubmitWanted,
  } = useInvokeAndSavePreference();
  // 캘린더 저장 Mutation 훅
  const {
    mutateAsync: saveCalendarData,
    isPending: isSaveCalendarPending,
    isSuccess: isSaveCalendarSuccess,
    isError: isSaveCalendarError,
    reset: resetSaveCalendar,
  } = useSaveCalendarData();

  const { mutateAsync: finalSubmitMutation, isPending: isFinalSubmitPending } =
    useFinalSubmit();

  const {
    mutateAsync: retractSubmissionMutation,
    isPending: isRetractPending,
  } = useRetractSubmission();

  const isPending =
    isSubmitWantedPending ||
    isSaveCalendarPending ||
    isFinalSubmitPending ||
    isRetractPending;
  const isSuccess = isSubmitWantedSuccess || isSaveCalendarSuccess;
  const isError = isSubmitWantedError || isSaveCalendarError;

  const dailyMaxRequests = useMemo(() => {
    const map: Record<string, number> = {};
    if (wantedConfig && Array.isArray(wantedConfig)) {
      wantedConfig.forEach((item: any) => {
        if (item.target_date && item.max_requests > 0) {
          map[item.target_date] = item.max_requests;
        }
      });
    }
    return map;
  }, [wantedConfig]);

  // TODO : OFF/휴가/휴무/연차인 근무코드 추출 (제한 걸 때 사용) 일단은 이렇게 적용하고 추후 기획 변경될 수도 있음.
  const targetShiftIds = useMemo(() => {
    return availableShifts
      .filter(
        (s: any) =>
          ["OFF", "휴가", "휴무", "연차"].includes(s.type) || s.shift_id === "O"
      )
      .map((s) => s.shift_id);
  }, [availableShifts]);

  // 일자별 신청 수 집계 (currentCount 계산) - 실시간 반영을 위해 useMemo 사용
  const dailyRequestCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    try {
      if (!preferenceSchedule || !availableShifts || !activeDate) return counts;

      // 1. 서버 데이터(preferenceSchedule)에서 '나'를 제외한 다른 사람들의 신청 내역 집계
      if (Array.isArray(preferenceSchedule)) {
        preferenceSchedule.forEach((nurse) => {
          // if (nurse.nurse_id === loginUser?.nurse_id) return; // 내 데이터 포함 (제출된 데이터 기준)

          nurse.schedule.forEach((shiftCode, dayIndex) => {
            if (shiftCode && targetShiftIds.includes(shiftCode)) {
              const day = dayIndex + 1;
              const dateStr = `${activeDate.year}-${String(activeDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              counts[dateStr] = (counts[dateStr] || 0) + 1;
            }
          });
        });
      }
    } catch (error) {
      console.error("dailyRequestCounts calculation error:", error);
    }
    return counts;
  }, [preferenceSchedule, availableShifts, activeDate, targetShiftIds]);

  useEffect(() => {
    if (!nurseList || !Array.isArray(nurseList)) return;
    const ordered = sortNurseListByRoleThenSequence([...nurseList]);
    const extract = ordered.map(({ nurse_id, name }) => ({
      nurse_id,
      name,
    }));
    setFullNurseSchema(ordered);
    setNurseSchema_IdName(extract);
  }, [nurseList]);

  const currentNurse = useMemo(() => {
    return fullNurseSchema.find((n) => n.nurse_id === loginUser?.nurse_id);
  }, [fullNurseSchema, loginUser]);

  useEffect(() => {
    if (wantedList && Array.isArray(wantedList)) {
      setFullWanted(wantedList);
    }
  }, [wantedList]);

  useLayoutEffect(() => {
    if (!wantedList?.length) return;

    const syncKey = `${gid}|${location.search}`;
    if (appliedWantedUrlKey.current === syncKey) return;

    const params = new URLSearchParams(location.search);
    const qy = params.get("year");
    const qm = params.get("month");

    if (qy && qm) {
      const y = parseInt(qy, 10);
      const m = parseInt(qm, 10);
      if (!Number.isNaN(y) && !Number.isNaN(m)) {
        const matched = wantedList.find(
          (w) => Number(w.year) === y && Number(w.month) === m
        );
        if (matched) {
          setActiveDate({ year: y, month: m });
          appliedWantedUrlKey.current = syncKey;
          return;
        }
        toast.error(`${y}년 ${m}월 원티드를 찾을 수 없습니다.`, {
          description:
            "요청 가능한 기간이 아니거나 잘못된 경로일 수 있습니다. 가장 최근 요청 기간을 표시합니다.",
        });
        setActiveDate({
          year: Number(wantedList[0].year),
          month: Number(wantedList[0].month),
        });
        appliedWantedUrlKey.current = syncKey;
        return;
      }
    }

    setActiveDate({
      year: Number(wantedList[0].year),
      month: Number(wantedList[0].month),
    });
    appliedWantedUrlKey.current = syncKey;
  }, [wantedList, location.search, gid]);

  function preferenceToSchedule(
    arrayLength: number,
    originArray: type.PreferencesAll | undefined
  ) {
    const result = Array(arrayLength).fill(null);
    if (originArray && originArray.data.shift) {
      for (const [letter, dates] of Object.entries(originArray.data.shift)) {
        for (const day of Object.keys(dates).map(Number)) {
          result[day - 1] = letter;
        }
      }
    }
    return result;
  }

  /* 근무 희망사항 제출  */
  // mutate로 수정해야함
  function submitRequest() {
    if (!activeDate) return;
    // 중복 호출 방지: 이미 요청 중이면 무시
    if (isSubmitRequest) {
      return;
    }
    if (!wantedInput) {
      alert("희망 근무 내용을 입력해주세요.");
      return;
    }

    setIsSubmitRequest(true);

    submitWantedRequest(
      { wantedInput, fullNurseSchema, activeDate, groupId: gid },
      {
        onSuccess: async (data) => {
          // 알림 리스트 캐싱 무효화 (팝오버, 모달 모두)
          await queryClient.invalidateQueries({ queryKey: ["alarms"] });

          setTimeout(() => {
            setIsSubmitRequest(false);
            resetSubmitWanted();

            // 백엔드 응답 구조: data.response.warning
            const warning = (data as { response?: { warning?: Warning } })
              ?.response?.warning;
            if (warning) {
              setConfirmModal({ type: "warning", warning });
            }
          }, 1000);
        },
      }
    );
  }

  /* 캘린더 저장 */
  const submitCalendarData = async (
    overrideData?: Type_SelectCalendar | React.MouseEvent,
    isReset: boolean = false
  ) => {
    if (!activeDate) return;
    // 중복 호출 방지: 이미 요청 중이면 무시
    if (isSubmitRequest) {
      return;
    }
    setIsSubmitRequest(true);

    // overrideData가 이벤트 객체가 아니고 데이터 객체일 경우 사용, 아니면 state 사용
    const isEvent = overrideData && (overrideData as any).nativeEvent;
    const targetData =
      overrideData && !isEvent
        ? (overrideData as Type_SelectCalendar)
        : selectedCalendarData;

    try {
      await saveCalendarData({
        fullNurseSchema,
        targetData,
        activeDate,
        isReset,
        preferenceData,
        groupId: gid,
      });
      setTimeout(() => {
        setIsSubmitRequest(false);
        resetSaveCalendar();
      }, 1000);
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "저장 중 오류가 발생했습니다.";
      console.error("submitCalendarData error:", errorMsg);
      setIsSubmitRequest(false); // 에러 발생 시 모달 닫기 (Toast로 에러 표시)
      throw error; // 상위 호출자(handleReset 등)에게 에러 전파
    }
  };

  // 최종 제출
  const finalSubmit = async () => {
    if (!activeDate) return;
    if (!confirm("최종 제출하시겠습니까?")) return;

    try {
      await submitCalendarData();

      await finalSubmitMutation({
        year: activeDate.year,
        month: activeDate.month,
        groupId: gid,
      });

      toast.success("성공적으로 제출되었습니다.");
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "제출 중 오류가 발생했습니다.";
      console.error("finalSubmit error:", errorMsg);
      toast.error(errorMsg);
    }
  };

  // 제출 철회
  const retractSubmission = async () => {
    if (!activeDate) return;
    if (!confirm("제출을 철회하고 수정하시겠습니까?")) return;

    try {
      await retractSubmissionMutation({
        year: activeDate.year,
        month: activeDate.month,
        groupId: gid,
      });

      toast.success("제출이 철회되었습니다. 다시 수정할 수 있습니다.");
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "철회 중 오류가 발생했습니다.";
      console.error("retractSubmission error:", errorMsg);
      toast.error(`철회에 실패했습니다: ${errorMsg}`);
    }
  };

  // 리셋
  const handleReset = async () => {
    if (!activeDate) return;
    if (!confirm("초기화하시겠습니까?")) {
      return;
    }
    try {
      // 일단 state만 초기화
      setSelectedCalendarData({});
      setFormattedCalendarData({});
      setWantedInput("");
      setSelectedShiftType("");

      // 빈 데이터로 저장 (재사용)
      await submitCalendarData({}, true);
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.preferences.latest(
          activeDate.year,
          activeDate.month,
          gid
        ),
      });
      toast.success("초기화되었습니다.");
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : "초기화 중 오류가 발생했습니다.";
      console.error("handleReset error:", errorMsg);
      toast.error(errorMsg);
    }
  };

  const discardChanges = useCallback(() => {
    setSelectedCalendarData(initialSelectedCalendarData);
    setWantedInput(initialWantedInput);
    setHasUnsavedChanges(false);
  }, [initialSelectedCalendarData, initialWantedInput]);

  const { showNavigationModal, proceedNavigation, handleCancelNavigation } =
    useUnsavedChanges(hasUnsavedChanges, discardChanges);

  const handleSaveAndNavigate = async () => {
    await submitCalendarData();
    setHasUnsavedChanges(false);
    proceedNavigation();
  };

  useEffect(() => {
    if (isLoading) return;
    const calendarChanged =
      JSON.stringify(initialSelectedCalendarData) !==
      JSON.stringify(selectedCalendarData);
    const inputChanged = (initialWantedInput || "") !== (wantedInput || "");
    setHasUnsavedChanges(calendarChanged || inputChanged);
  }, [
    selectedCalendarData,
    wantedInput,
    initialSelectedCalendarData,
    initialWantedInput,
    isLoading,
  ]);

  const handleShiftChangeRequest = (dateStr: string, shiftType: string) => {
    setConfirmModal({ type: "deleteShift", date: dateStr, shiftType });
  };

  const handleCalendarDateClick = async (dateStr: string) => {
    if (submittedStatus !== "requested") return;

    if (!selectedShiftType.trim()) {
      toast.info("근무 유형을 먼저 선택해 주세요.");
      return;
    }

    const currentData = selectedCalendarData[dateStr];
    const isWeeklyOff = currentData?.comment === "주휴";

    if (isWeeklyOff) return;

    // 개인별 원티드 개수 제한 체크
    const maxPersonalWanted = currentNurse?.wanted_max_requests ?? null;

    const isTargetShift = targetShiftIds.includes(selectedShiftType);

    if (isTargetShift && maxPersonalWanted !== null) {
      let myCurrentCount = 0;
      Object.values(selectedCalendarData).forEach((entry) => {
        if (entry.type && targetShiftIds.includes(entry.type)) {
          myCurrentCount++;
        }
      });

      const isAlreadyCounted =
        currentData?.type && targetShiftIds.includes(currentData.type);

      if (!isAlreadyCounted) {
        if (myCurrentCount >= maxPersonalWanted) {
          setConfirmModal({ type: "totalExceeded" });
          return;
        }
      }
    }

    // 근무코드의 type이 휴가, 연차, 휴무인 경우에만 유효성 검사 수행
    const targetShift = availableShifts.find(
      (s) => s.shift_id === selectedShiftType
    );
    const shouldValidate =
      targetShift &&
      ["휴가", "연차", "휴무"].includes((targetShift as any).type);

    if (shouldValidate) {
      const maxCount = dailyMaxRequests[dateStr];
      if (maxCount !== undefined) {
        const currentCount = dailyRequestCounts[dateStr] || 0;

        // 내가 이미 제출한 스케줄인지 확인
        const day = parseInt(dateStr.split("-")[2]);
        const mySubmittedSchedule = preferenceSchedule?.find(
          (n) => n.nurse_id === loginUser?.nurse_id
        )?.schedule;
        const mySubmittedShift = mySubmittedSchedule?.[day - 1];
        const isAlreadySubmittedTargetShift =
          mySubmittedShift && targetShiftIds.includes(mySubmittedShift);

        if (!isAlreadySubmittedTargetShift) {
          if (currentCount >= maxCount) {
            setConfirmModal({ type: "dateExceeded", date: dateStr });

            return;
          }
        }
      }
    }

    const hasSavedCommentValue = !!currentData?.comment && !isWeeklyOff;
    if (hasSavedCommentValue) {
      handleShiftChangeRequest(dateStr, selectedShiftType);
    } else {
      setSelectedCalendarData((prev) => ({
        ...prev,
        [dateStr]: { type: selectedShiftType, comment: null },
      }));
    }
  };

  // 근무코드 변경 확인 핸들러
  const handleConfirmShiftChange = () => {
    if (confirmModal?.type === "deleteShift") {
      setSelectedCalendarData((prev) => ({
        ...prev,
        [confirmModal.date]: {
          type: confirmModal.shiftType,
          comment: null,
        },
      }));
      setConfirmModal(null);
    }
  };

  // 휴일 세팅
  useEffect(() => {
    if (holidayDatesData) setHolidayDates(holidayDatesData);
  }, [holidayDatesData]);

  useEffect(() => {
    if (shiftsData) {
      setAvailableShifts(shiftsData);
    }
  }, [shiftsData]);

  // 주휴 세팅
  useEffect(() => {
    setMyWeeklyOffIndex(myWeeklyOffData?.my_weekly_off_weekday ?? null);
  }, [myWeeklyOffData]);

  useEffect(() => {
    if (!activeDate) return;

    if (isCurrentWantedClosed) {
      setSubmittedStatus("closed");
      setPreferenceData(null);
      setWantedInput("");
      setInitialWantedInput("");
      setFormattedCalendarData({});
      setSelectedCalendarData({});
      setInitialSelectedCalendarData({});
      return;
    }

    if (preferencesLatestData) {
      setPreferenceData(preferencesLatestData.preference_data);
      setWantedInput(preferencesLatestData.preference_data?.request ?? "");
      setInitialWantedInput(
        preferencesLatestData.preference_data?.request ?? ""
      );
      setSubmittedStatus(
        preferencesLatestData.is_submitted ? "submitted" : "requested"
      );

      const calendarData: CalendarData = {};
      if (preferencesLatestData.preference_data?.shift) {
        const shifts = preferencesLatestData.preference_data.shift;
        const maps = shiftLookupMaps;
        for (const [shiftType, dates] of Object.entries(shifts)) {
          if (!dates || typeof dates !== "object") continue;
          const dateList = Array.isArray(dates) ? dates : Object.keys(dates);
          for (const dateStr of dateList) {
            let formattedDate;
            if (dateStr.includes("-")) {
              formattedDate = dateStr;
            } else {
              const day = parseInt(dateStr, 10);
              formattedDate = `${activeDate.year}-${String(
                activeDate.month
              ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            }
            const cell = (dates as Record<string, type.PreferenceShiftDayCell>)[
              dateStr
            ];
            if (!cell) continue;
            const comment =
              cell.comment === null || cell.comment === undefined
                ? ""
                : String(cell.comment);
            const pk = cell.shifts_table_id;
            /** `Roster_create` / `row_NurseRosterList`와 동일: `wanted-shift-util` → `resolveShiftFromScheduleCellFromMaps` */
            const resolved = maps
              ? resolveShiftFromWantedEntry(
                  { shift_id: shiftType, shifts_table_id: pk ?? null },
                  maps
                )
              : undefined;
            const displayShiftId = resolved?.shift_id ?? shiftType;
            calendarData[formattedDate] = {
              shift: displayShiftId,
              comment,
            };
          }
        }
      }
      setFormattedCalendarData(calendarData);
      const converted: Type_SelectCalendar = Object.fromEntries(
        Object.entries(calendarData).map(([date, value]) => {
          const { shift, ...rest } = value;
          return [date, { ...rest, type: shift, comment: value.comment }];
        })
      );
      setSelectedCalendarData(converted);
      setInitialSelectedCalendarData(converted);
    } else if (!isLoading) {
      setPreferenceData(null);
      setWantedInput("");
      setInitialWantedInput("");
      setSubmittedStatus(currentWanted ? "requested" : "empty");
      setFormattedCalendarData({});
      setSelectedCalendarData({});
      setInitialSelectedCalendarData({});
    }
  }, [
    activeDate,
    preferencesLatestData,
    isLoading,
    isCurrentWantedClosed,
    currentWanted,
    shiftLookupMaps,
  ]);

  useEffect(() => {
    if (preferencesAllData && fullNurseSchema.length > 0 && activeDate) {
      const arrayLength = new Date(
        activeDate.year,
        activeDate.month,
        0
      ).getDate();
      const fullNurse_preference = fullNurseSchema.map((nurse) => {
        const found = preferencesAllData.find(
          (p) => p.nurse_id === nurse.nurse_id
        );
        return {
          nurse_id: nurse.nurse_id,
          schedule: preferenceToSchedule(arrayLength, found),
        };
      });
      setPreferenceSchedule(fullNurse_preference);
    } else if (!isLoading) {
      // isPreferencesAllLoading 대체
      setPreferenceSchedule([]);
    }
  }, [preferencesAllData, isLoading, fullNurseSchema, activeDate]);

  useEffect(() => {
    if (availableShifts.length > 0) {
      const filteredShifts = availableShifts.filter((item: any) => {
        if (!isUseMid && item.default_shift === "M") return false;
        const isDefaultShiftExcluding주 =
          item.default_shift && item.default_shift !== "주";
        return (
          (isDefaultShiftExcluding주 || item.shift_gb === null) &&
          item.show_in_preference
        );
      });
      setDefaultShiftCode(filteredShifts);
    }
  }, [availableShifts, isUseMid]);

  useEffect(() => {
    const textarea = document.getElementById(
      "wanted-input"
    ) as HTMLTextAreaElement;
    if (textarea) {
      adjustTextareaHeight(textarea);
    }
  }, [wantedInput]);

  if (pageError) {
    const errorMsg =
      pageError instanceof Error
        ? pageError.message
        : "데이터를 불러오는 중 오류가 발생했습니다.";
    return <ErrorComponent errorMsg={errorMsg} />;
  }

  if (!isLoading && wantedList && wantedList.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <Info className="w-16 h-16 text-[#939399] mb-4" />
        <h2 className="text-[20px] font-bold text-[#939399] mb-2">
          요청 가능한 원티드 기간이 없습니다.
        </h2>
        <p className="text-[#939399] font-pretendard">
          수간호사에게 문의해주세요.
        </p>
      </div>
    );
  }

  if (isLoading || !activeDate) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <LoadingDots />
      </div>
    );
  }

  return (
    <div className="!pt-[60px] w-[calc(100%-100px)] mx-auto py-[65px] ">
      <div className="w-full ">
        {loginUser && (
          <HeadNurseHeader
            wantedButtonRef={wantedButtonRef}
            setIsOpendDeadlineModal={() =>
              setConfirmModal({ type: "deadline" })
            }
            isSubmitRequest={isSubmitRequest}
            isFinalSubmitButtonClick={isFinalSubmitPending}
            isExistWanted={isExistWanted}
            activeDate={activeDate}
            wantedList={wantedList}
            setActiveDate={setActiveDateSynced}
            selectedGroupId={selectedGroupId}
          />
        )}
      </div>

      <div className="w_gridWrap !gap-x-[29px] mb-5">
        <section
          className="w_myWanted__section min-w-[680px]"
          style={{ gridArea: "manual" }}
        >
          <header
            className={`text-white text-[18px] font-bold p-5 text-center bg-sig rounded-tr-[10px] rounded-tl-[10px] font-pretendard ${submittedStatus === "closed" && "!bg-[#d6d6de]"}`}
          >
            <h2>근무 코드 직접 입력하기</h2>
          </header>

          <div
            className={`w_myWanted__sectionBody flex flex-col items-center !p-[34px] !pt-[27px] ${submittedStatus === "closed" && "text-[#939399] select-none"}`}
          >
            <div className="mb-[25px]">
              아래 근무 유형을 선택하고 캘린더에서 원하는 날짜를 선택하여
              입력하세요.
            </div>

            <div className="">
              <div className="flex flex-col justify-center bg-[#E0E0E6] w-full gap-[1px]">
                {/* 근무 유형 */}
                <div
                  className={`grid grid-cols-1 bg-white w-full items-center py-[13px] px-[34px] ${defaultShiftCode && defaultShiftCode.length > 0 && "gap-[33px]"}`}
                >
                  <div className="flex justify-center flex-wrap gap-x-[30px] gap-y-[15px] items-start max-w-full justify-self-center">
                    {defaultShiftCode && defaultShiftCode.length > 0 ? (
                      defaultShiftCode.map((shift, index) => {
                        const isChecked = selectedShiftType === shift.shift_id;
                        return (
                          <SelectShiftTypeButton
                            key={index}
                            isChecked={isChecked}
                            setSelectedShiftType={setSelectedShiftType}
                            shift_id={shift.shift_id}
                            name={shift.name}
                            color={shift.color}
                            isExistWanted={isExistWanted}
                            disabled={submittedStatus === "closed"}
                          />
                        );
                      })
                    ) : (
                      <div className="text-text-sub font-pretendard text-[15px]">
                        선택 가능한 근무 유형이 없습니다.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {currentNurse?.enable_aide && (
          <section
            className="mt-[19px]"
            style={{
              gridArea: "llm",
            }}
          >
            <div className="flex justify-center mb-[21px]">
              <img
                src={images.icon_logo_medium}
                alt=""
                style={{
                  display: "inline-block",
                  verticalAlign: "baseline",
                }}
              />
              <span className="self-end font-pretendard font-bold text-text-main2 text-[18px]">
                가 입력하기
              </span>
            </div>

            <div className="flex justify-center mb-[18px]">
              <span className="font-pretendard text-text-main2 text-[18px]">
                에이드에게 원티드를 작성해주시면 캘린더에 입력해드릴게요.
              </span>
            </div>

            <div
              className={`flex bg-white rounded-full px-[18px] py-[11px] justify-between items-center ${
                (isFocusedInTextarea || wantedInput) &&
                submittedStatus !== "closed"
                  ? "active"
                  : "unActive"
              }`}
            >
              <div className="flex items-center pl-[17px] w-full [text-align-last:start]">
                <textarea
                  id="wanted-input"
                  className="text-[15px] w-full text-text-sub text-lg leading-[1.2] font-pretendard h-[1.2em] border-0 resize-none px-4 font-light placeholder:text-center placeholder:font-medium placeholder:text-[16px] placeholder:font-pretendard placeholder:text-text-sub"
                  rows={1}
                  placeholder={
                    isExistWanted
                      ? submittedStatus === "closed"
                        ? `${activeDate?.year}년 ${activeDate?.month}월 wanted 작성이 마감되었습니다`
                        : submittedStatus === "submitted"
                          ? "근무 희망사항을 제출하셨습니다. 수정을 원하시면 아래 수정하기 버튼을 눌러주세요"
                          : "캘린더에 입력할 날짜,근무코드,사유를 작성해주세요. (사유는 필수사항이 아닙니다.)"
                      : "요청된 wanted가 없습니다"
                  }
                  value={
                    submittedStatus === "closed" ? "" : (wantedInput ?? "")
                  }
                  onFocus={() => {
                    setIsFocusedInTextarea(true);
                  }}
                  onBlur={() => {
                    setIsFocusedInTextarea(false);
                  }}
                  onChange={handleTextareaChange}
                  onKeyDown={(e) => {
                    func.handleKeydown(e, submitRequest);
                  }}
                  disabled={
                    submittedStatus === "closed" ||
                    submittedStatus === "submitted"
                  }
                />
              </div>

              <button
                type="button"
                className={cn(
                  "w_submitButton !w-[70px] !h-[70px] !rounded-full !flex !justify-center !items-center",
                  (submittedStatus === "closed" ||
                    (!wantedInput && !isFocusedInTextarea)) &&
                    "cursor-default!",
                  (isSubmitRequest ||
                    submittedStatus === "closed" ||
                    (!wantedInput && !isFocusedInTextarea)) &&
                    "pointer-events-none !bg-[image:none] !bg-[#D9D9D9]"
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={submitRequest}
                disabled={isSubmitRequest || submittedStatus === "closed"}
              >
                <ArrowRight size={50} />
              </button>
            </div>
          </section>
        )}

        <div style={{ gridArea: "calendar", height: "min-content" }}>
          <section className="w_myWanted__section min-w-[680px]">
            <header
              className={`w_myWanted__sectionHeader ${submittedStatus === "closed" && "!bg-[#d6d6de]"}`}
            >
              <h2>원티드 캘린더</h2>
            </header>

            <div className="w_myWanted__sectionBody d-f fd-c ai-c relative">
              {/* 일반간호사 / 수간호사 다 노출로 변경 */}
              <div className="flex justify-between flex-col w-full ">
                <div className="mx-auto w-[650px] gap-5 flex justify-between items-center pb-5">
                  <div className="grid grid-cols-[250px_2fr] gap-4 relative">
                    {loginUser && (
                      <div className="flex shadow-2xl items-center py-2 px-4 border border-zinc-100 rounded-[5px]">
                        <div className="flex flex-col mr-5 gap-1">
                          <span className="font-pretendard text-text-main2 text-lg font-bold">
                            {loginUser.name}
                          </span>
                          <span className="font-pretendard text-text-sub text-[14px] font-medium">
                            신청 가능한 휴가·휴무
                          </span>
                        </div>

                        <div className="flex items-end">
                          {currentNurse?.wanted_max_requests != null ? (
                            <>
                              <span className="font-pretendard text-sig text-3xl font-medium mr-[2px] leading-none">
                                {currentNurse?.wanted_max_requests}
                              </span>
                              <span className="font-pretendard text-[14px] text-text-sub font-medium">
                                개
                              </span>
                            </>
                          ) : (
                            <span className="font-pretendard text-sig text-lg font-medium leading-none">
                              제한 없음
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col right-[-10px]  bg-white  left-[270px] top-0 ">
                      <span className="font-pretendard text-text-sub font-normal mb-[4px] text-sm ">
                        * 주휴는 원티드 신청개수에 미포함됩니다.
                      </span>
                      <span className="font-pretendard text-text-sub font-normal mb-[4px] text-sm">
                        * 캘린더에 표시된 숫자는 휴가, 휴무 신청 최대 개수를
                        의미합니다.
                      </span>
                      <span className="font-pretendard text-text-sub font-normal mb-[4px] text-sm">
                        * ‘제출 완료’한 원티드만 근무표에 반영됩니다.
                      </span>
                    </div>
                  </div>
                </div>

                <TableSelectDate
                  activeDate={activeDate}
                  holidayDates={holidayDates}
                  availableShifts={availableShifts}
                  formattedCalendarData={formattedCalendarData}
                  selectedCalendarData={selectedCalendarData}
                  setSelectedCalendarData={setSelectedCalendarData}
                  submittedStatus={submittedStatus}
                  selectedShiftType={selectedShiftType}
                  isExistWanted={isExistWanted}
                  myWeeklyOff={myWeeklyOffIndex}
                  enableHighlightNew={true} // 새로운 근무코드 border 표시 여부
                  enableCommentInput={true} // 사유작성 말풍선 인풋 출력 여부
                  onShiftChangeRequest={handleShiftChangeRequest}
                  dailyMaxRequests={dailyMaxRequests}
                  dailyRequestCounts={dailyRequestCounts}
                  onDateClick={handleCalendarDateClick}
                />
              </div>

              <div className="mt-10">
                {currentNurse?.enable_nurse_pair_preference && (
                  <PreferenceList
                    preferenceData={preferenceData ?? undefined}
                    nurseSchema_IdName={nurseSchema_IdName}
                    submittedStatus={submittedStatus}
                  />
                )}
              </div>
            </div>
          </section>

          <div className="flex justify-end mt-[48px]">
            {/* 초기화 버튼 */}
            {(submittedStatus === "empty" ||
              submittedStatus === "requested") && (
              <SoftPillButton
                type="button"
                pillVariant="interactive"
                className="!mr-[8px]"
                onClick={handleReset}
                disabled={
                  Object.keys(selectedCalendarData).length === 0 &&
                  !wantedInput &&
                  (!preferenceData?.preference ||
                    preferenceData.preference.length === 0)
                }
              >
                <span
                  className={cn(softPillButtonLabelClassName, "text-[#939399]")}
                >
                  초기화
                </span>
              </SoftPillButton>
            )}

            {canViewAllStaffWantedOverview && (
              <SoftPillButton
                type="button"
                className="justify-self-end"
                onClick={() => {
                  setConfirmModal({
                    type: "submittedList",
                    isViewOnlyOff: false,
                    showWeeklyOff: false,
                  });
                }}
              >
                <span className={cn(softPillButtonLabelClassName, "text-sig")}>
                  전체 근무자 원티드 요청 현황 보기
                </span>
              </SoftPillButton>
            )}
          </div>
        </div>
      </div>

      {/* 반응형으로 움직이지 않고 레이아웃의 중앙에 고정되도록 일단 min-w-[1500px](최소 너비 설정)  */}
      <div className="flex !flex-col w-full min-w-[1500px]">
        <div className="flex justify-center mt-[21px]">
          {(submittedStatus === "empty" || submittedStatus === "requested") && (
            <button
              className={`w_finalSubmitButton w_finalSubmitButton--draft !mr-[10px]`}
              onClick={submitCalendarData}
              disabled={isSubmitRequest || isFinalSubmitPending}
            >
              임시저장
            </button>
          )}

          {!(submittedStatus === "closed") && (
            <button
              className={`w_finalSubmitButton  ${
                submittedStatus === "submitted" && "w_finalSubmitButton--draft"
              }`}
              onClick={
                submittedStatus === "submitted"
                  ? retractSubmission
                  : finalSubmit
              }
              disabled={isSubmitRequest || isFinalSubmitPending}
            >
              {submittedStatus === "submitted" ? "수정하기" : "제출하기"}
            </button>
          )}
        </div>
      </div>

      {/* Portal로 모달 렌더링 */}
      {isSubmitRequest &&
        createPortal(
          <div
            className="absolute inset-0 bg-black/85 flex flex-col justify-center items-center z-[9999]"
            style={{
              position: "absolute",
              top: `${modalScrollTop}px`,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100vh",
            }}
          >
            {isPending ? (
              <>
                <p className="text-white text-2xl font-bold mb-5">
                  희망사항을 확인하고 있어요
                </p>
                <LoadingDots />
              </>
            ) : isSuccess ? (
              <p className="text-white text-2xl font-bold">저장되었습니다.</p>
            ) : isError ? (
              <div className="flex flex-col items-center">
                <p className="text-white text-2xl font-bold mb-3">
                  저장에 실패했습니다.
                </p>
                <p className="text-white text-xl mb-5">
                  실패가 반복되면 고객센터에 문의해주세요.
                </p>
                <button
                  type="button"
                  className="px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    setIsSubmitRequest(false);
                    resetSubmitWanted();
                    resetSaveCalendar();
                  }}
                >
                  확인
                </button>
              </div>
            ) : null}
          </div>,
          document.querySelector(".c_main__container") as HTMLElement
        )}

      {confirmModal?.type === "submittedList" &&
        activeDate &&
        canViewAllStaffWantedOverview && (
          <ModalSubmittedWantedlist
            defaultShiftCode={defaultShiftCode ?? []}
            currentScheduleInfo={{ ...activeDate, version: 0 }}
            holidayDates={holidayDates}
            fullWanted={fullWanted}
            preferenceSchedule={preferenceSchedule ?? []}
            shiftInfo={availableShifts}
            nurses={fullNurseSchema}
            onClose={() => setConfirmModal(null)}
            isViewOnlyOff={confirmModal.isViewOnlyOff}
            showWeeklyOff={confirmModal.showWeeklyOff}
            loginUser={loginUser}
          />
        )}

      {/* 수간호사 전용 모달들 */}
      {showNavigationModal && (
        <CustomModal.Confirm
          isOpen={showNavigationModal}
          onClose={handleCancelNavigation}
          onConfirm={handleSaveAndNavigate}
          isPending={isSubmitRequest}
          title={`저장되지 않은 변경사항이 있습니다. \n저장하시겠습니까?`}
          pendingMsg="저장 중..."
        />
      )}

      {/* 원티드 요청 모달 */}
      {loginUser &&
        loginUser.is_head_nurse &&
        confirmModal?.type === "deadline" &&
        wantedStatus && (
          <SetDeadLine
            setIsOpendDeadlineModal={() => setConfirmModal(null)}
            wantedStatus={wantedStatus}
            fetchWantedStatus={(year: number, month: number) => {
              // useWantedStatusQuery가 자동으로 처리하므로 refetch만 수행
              queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.wanted.status(year, month, gid),
              });
            }}
            setIsOpendModalSubmittedWantedlistModal={(value) => {
              if (value) {
                setConfirmModal({
                  type: "submittedList",
                  isViewOnlyOff: false,
                  showWeeklyOff: true,
                });
              }
            }}
            targetRef={wantedButtonRef}
            currentScheduleInfo={
              activeDate
                ? {
                    year: activeDate.year,
                    month: activeDate.month,
                    version: 0,
                  }
                : undefined
            }
          />
        )}

      {/* 단순 모달들 */}
      {/* 원티드 일자 개수 초과 모달 */}
      {confirmModal?.type === "dateExceeded" && (
        <CustomModal.Alert
          isOpen
          onClose={() => setConfirmModal(null)}
          title={`${Number(confirmModal.date.split("-")[1])}월 ${Number(confirmModal.date.split("-")[2])}일 신청 가능한 원티드 개수를\n초과하였습니다.`}
        />
      )}

      {/* 원티드 총 개수 초과 모달 */}
      {confirmModal?.type === "totalExceeded" && (
        <CustomModal.Alert
          isOpen
          onClose={() => setConfirmModal(null)}
          title={`신청가능한 휴가·휴무 개수(${currentNurse?.wanted_max_requests}개)를\n초과하였습니다.`}
        />
      )}

      {confirmModal?.type === "warning" && (
        <CustomModal.Alert
          isOpen
          onClose={() => setConfirmModal(null)}
          title={confirmModal.warning.message}
        />
      )}
      <CustomModal.Confirm
        isOpen={confirmModal?.type === "deleteShift"}
        onClose={() => setConfirmModal(null)}
        onConfirm={handleConfirmShiftChange}
        title={`근무 코드를 변경할까요?\n변경 시 사유가 삭제됩니다.`}
      />
    </div>
  );
}
